// src/components/crm/JobQuickViewModal.js

import React, { useState, useEffect, useRef } from 'react';
import LineDescription from '../documents/LineDescription'; // linetext-4d8b2e61 — line descriptions keep line breaks, indents and bullets
import { LINE_NAME, LINE_DESC } from '../documents/lineItemStyles'; // linetext-4d8b2e61 — the PDF's line layout
// r1-3i-jqvm-imports-3d8b1e05 — Notes & Media section deps
import useOrgNotes from '../../hooks/useOrgNotes';
import NoteComposer from './NoteComposer';
import NoteCard from './NoteCard';
// wppromote-6a3f1e94 — Seam 3 (Promote): append into Work Performed, mark on save.
// wpauthor-5d3f8a26 — promotionsSurvived is no longer imported here: with entries
// the question it guessed at ("did the promoted text survive a reword?") is exact.
import { appendToWorkPerformed, markNotesPromoted } from '../../utils/promoteNote';
import useWorkPerformedEntries from '../../hooks/useWorkPerformedEntries';
import { isActiveEntry, describeWorkPerformedAuthor, WP_NO_AUTHOR_LABEL } from '../../utils/workPerformedEntries';
import { canEditAnyWorkPerformedEntry } from '../../utils/permissions';
import { formatJobNumber } from '../../utils/formatters'; // jobnumdisp-6a1e9c34
import { assignedToList } from '../../utils/calendarUnassign'; // calclar-5e2a8d14
// chklstweb-3a7f1c58 — Checklists panel + completion gate (Seam 3)
import useJobChecklists from '../../hooks/useJobChecklists';
import JobChecklistsPanel from '../checklists/JobChecklistsPanel';
import ChecklistGateDialog from '../checklists/ChecklistGateDialog';
import { readChecklistGate, explainChecklistRefusal, overrideChecklistGate, canOverrideChecklist } from '../../utils/checklistClient';
// jobtidy-4d8b1f63 — Phone and CalendarPlus dropped: they were only used by the
// deleted legacy note composer's flag-type buttons and follow-up checkbox.
import { X, Play, Square, Edit, Clock, CheckCircle, AlertTriangle, LogIn, Plus, FileText, Lock, Eye, ChevronDown, ChevronUp, Flag, Wrench, MessageSquare, Truck } from 'lucide-react';
import { isSameDay, format, isWithinInterval, addHours, subHours } from 'date-fns';
import TaxExemptionBanner, { withExemptionRefresh } from '../common/TaxExemptionBanner'; // taxexempt3-8c4f2a1d — withExemptionRefresh: the server's new figures right after a switch-off

const formatElapsedTime = (startTime) => {
    if (!startTime) return '00:00:00';
    const diff = new Date() - startTime;
    const seconds = Math.floor((diff / 1000) % 60);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const pad = (num) => num.toString().padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

const formatSessionDuration = (hours) => {
    if (hours === null || hours === undefined) return '—';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
};

const safeToDate = (val) => {
    if (!val) return null;
    if (val.toDate) return val.toDate();
    if (val instanceof Date) return val;
    return new Date(val);
};

const JobQuickViewModal = ({
    isOpen,
    onClose,
    job,
    currentUserId,
    currentUserName,
    canEdit,
    onStartTimer,
    onStopTimer,
    onCompleteJob,
    onEdit,
    // wpauthor-5d3f8a26 — REPLACE onSaveWorkPerformed (jobtidy-4d8b1f63), which
    // saved the whole box as one field with no record of who wrote what. Work
    // Performed is a list of AUTHORED entries now; each of these writes one entry
    // and the flattened mirror in a single batch (see CrmApp).
    onAddWorkPerformedEntry,
    onEditWorkPerformedEntry,
    onRemoveWorkPerformedEntry,
    onAddTechLineItem,
    orgUsers,
    currentUserProfile, // r1-3i-jqvm-props-9a4c2f81 — NoteComposer/NoteCard author + useOrgNotes scope
    orgId,              // r1-3i-jqvm-props-9a4c2f81
    resources,
    onUpdateResources,
    jobs,
}) => {
    const [elapsedTime, setElapsedTime] = useState('00:00:00');
    const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
    const [isCompleting, setIsCompleting] = useState(false);

    // ═══════════════════════════════════════════════════════════════════════
    // wpauthor-5d3f8a26 — WORK PERFORMED ATTRIBUTION, SEAM B (web).
    // ═══════════════════════════════════════════════════════════════════════
    // REPLACES the single `workPerformed` draft string (jobtidy-4d8b1f63). There
    // is no one box any more, so there is no one draft: each entry is its own
    // document with its own author, and the composer below is only ever used for
    // ONE entry at a time — the new one being added, or the one being edited.
    //
    // THE UNSAVED-DRAFT GUARD SURVIVES, and matters more than before. Each write
    // commits on its own, so an abandoned composer loses only what is in it — but
    // losing a promoted note's text silently is exactly the bug the old guard
    // existed to prevent, so the composer still counts as dirty.
    const [draftText, setDraftText] = useState('');       // the composer's text
    const [editingEntryId, setEditingEntryId] = useState(null); // null = adding
    const [isSavingEntry, setIsSavingEntry] = useState(false);
    const draftDirtyRef = useRef(false);

    // wppromote-6a3f1e94 + wpauthor-5d3f8a26 — the note ids promoted into the
    // composer but not yet saved. `promotionsSurvived` is GONE on this path: it
    // was a heuristic ("did the appended text survive a free-text edit?") that
    // its own comment admitted could not tell rewording from deleting. An entry
    // is a discrete document, so the question is now exact — the entry either got
    // written or it did not — and the marker is written iff the entry was.
    const [pendingPromotions, setPendingPromotions] = useState([]);

    // r1-3i-jqvm-wire-5e2a8b71 — Notes & Media (web QuickView). Reads
    // job-attached notes (job:<id>) for the live count + list; holds the
    // collapse toggle. Sits beside the legacy techNotes path — that is unchanged.
    const [jobNotesExpanded, setJobNotesExpanded] = useState(false);
    const { notes: allJobNotes } = useOrgNotes({ // chklstweb-3a7f1c58
        orgId: orgId || null,
        attachedKey: job?.id ? `job:${job.id}` : null,
    });
    // chklstweb-3a7f1c58 — a checklist photo/signature note shows its photos in the
    // Checklists panel; in this strip it would be an empty internal note.
    const jobNotes = allJobNotes.filter(n => !n.checklistSource);
    const isChecklistJob = (job?.eventType || 'Job') === 'Job';
    const { checklists: jobChecklists, state: checklistState } = useJobChecklists({
        orgId: orgId || null, jobId: job?.id || null, enabled: !!isOpen && isChecklistJob,
    });
    const [gateDialogOpen, setGateDialogOpen] = useState(false);
    const [checklistFocus, setChecklistFocus] = useState(null); // { checklistId, nonce }

    // wpauthor-5d3f8a26 — the entry list, live and in CUSTOMER order (oldest
    // first, the order the flatten uses). Includes removed entries: they are
    // rendered greyed, never filtered out — removal hides, it never erases.
    const { entries: workPerformedEntries } = useWorkPerformedEntries({
        orgId: orgId || null,
        jobId: job?.id || null,
    });

    // Add item state
    const [showAddItem, setShowAddItem] = useState(false);
    const [newItem, setNewItem] = useState({ name: '', quantity: 1, price: '' });
    const [isSavingItem, setIsSavingItem] = useState(false);

    // Resource editing state
    const [showEditResources, setShowEditResources] = useState(false);
    const [isSavingResources, setIsSavingResources] = useState(false);
    const assignedResources = job?.assignedResources || [];

    // --- Derive punch state from timePunches array ---
    const myPunches = (job?.timePunches || []).filter(p => p.userId === currentUserId);
    const activePunch = myPunches.find(p => p.punchOut === null);
    const closedPunches = myPunches.filter(p => p.punchOut !== null);
    const totalClockedHours = closedPunches.reduce((sum, p) => sum + (parseFloat(p.hours) || 0), 0);
    const hasAnyPunches = myPunches.length > 0;

    const legacyStartTime = safeToDate(job?.timeTrackingStartedAt);
    const isLegacyInProgress = job?.status === 'In Progress' && legacyStartTime && !job?.timePunches?.length;

    const activePunchInTime = activePunch ? safeToDate(activePunch.punchIn) : null;
    const clockRunning = !!activePunch || isLegacyInProgress;
    const clockStartTime = activePunchInTime || legacyStartTime;

    const isJobDone = job?.status === 'Completed' || job?.status === 'Canceled';

    // Tick
    useEffect(() => {
        let interval;
        if (isOpen && clockRunning && clockStartTime) {
            setElapsedTime(formatElapsedTime(clockStartTime));
            interval = setInterval(() => setElapsedTime(formatElapsedTime(clockStartTime)), 1000);
        } else {
            setElapsedTime('00:00:00');
        }
        return () => clearInterval(interval);
    }, [isOpen, clockRunning, clockStartTime]);

    // Reset state when modal closes or job changes
    useEffect(() => {
        setShowCompleteConfirm(false);
        setIsCompleting(false);
        setShowAddItem(false);
        setNewItem({ name: '', quantity: 1, price: '' });
        setShowEditResources(false);
        // wpauthor-5d3f8a26 — a new job (or a reopen) starts with an empty
        // composer and a clean dirty flag. Without clearing the ref here, a draft
        // abandoned on one job would make the NEXT job look unsaved.
        //
        // No seeding from job.workPerformed any more: the entry list IS the
        // record, and it arrives live from useWorkPerformedEntries. There is
        // therefore no "snapshot overwrites what I am typing" race left to guard
        // — a colleague adding an entry appears as a new row beneath, not as text
        // replacing the cursor. That second guarded effect is gone with the race.
        draftDirtyRef.current = false;
        setDraftText('');
        setEditingEntryId(null);
        setIsSavingEntry(false);
        setPendingPromotions([]);          // wppromote-6a3f1e94
        setGateDialogOpen(false);          // chklstweb-3a7f1c58
        setChecklistFocus(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, job?.id]);

    if (!isOpen || !job) return null;

    const handlePunchIn = () => {
        const jobDate = safeToDate(job.startAt);
        const today = new Date();
        if (jobDate && !isSameDay(jobDate, today)) {
            const formatted = jobDate.toLocaleDateString();
            if (!window.confirm(`This job is scheduled for ${formatted}. Start it now?`)) return;
        }
        onStartTimer(job.id);
    };

    const handlePunchOut = () => {
        const startTime = clockStartTime || new Date();
        onStopTimer(job.id, startTime);
    };

    // jobtidy-4d8b1f63 — the unsaved-draft guard. The Work Performed box has its
    // own Save button and there is no global save, so every path that tears the
    // modal down has to ask first. Returns true when it is safe to proceed.
    // window.confirm is what this file already uses (handlePunchIn below).
    // NOTE: the modal has no backdrop-click or Escape close today — the only exits
    // are the X, Edit Full Details, and completing the job — so those are the three
    // paths guarded. Any future close path must call this too.
    const confirmDiscardWorkPerformed = () => {
        if (!draftDirtyRef.current) return true;
        return window.confirm('Work Performed has unsaved changes. Discard them?');
    };

    const handleAttemptClose = () => {
        if (!confirmDiscardWorkPerformed()) return;
        onClose();
    };

    const handleEditFullDetails = () => {
        if (!confirmDiscardWorkPerformed()) return;
        onEdit(job);
    };

    // wppromote-6a3f1e94 — append into the COMPOSER only. This never saves: the
    // reviewer rewords the text and then presses Add. Setting the dirty ref is
    // load-bearing exactly as typing is — it warns on close.
    //
    // Promoting while EDITING an existing entry would append someone else's note
    // text into that entry under its original author's name, so promote targets
    // the new-entry composer and switches out of edit mode first.
    const handlePromoteNote = (note) => {
        setEditingEntryId(null);
        draftDirtyRef.current = true;
        setDraftText(prev => appendToWorkPerformed(prev, note.text));
        setPendingPromotions(prev => (prev.includes(note.id) ? prev : [...prev, note.id]));
    };

    const startEditEntry = (entry) => {
        if (!confirmDiscardWorkPerformed()) return;
        setPendingPromotions([]);  // promotions belong to the new-entry composer
        setEditingEntryId(entry.id);
        setDraftText(entry.text || '');
        draftDirtyRef.current = false;
    };

    const cancelEditEntry = () => {
        if (!confirmDiscardWorkPerformed()) return;
        setEditingEntryId(null);
        setDraftText('');
        draftDirtyRef.current = false;
    };

    // ONE door for the composer's Save, whether adding or editing.
    const handleSaveEntryClick = async () => {
        const text = (draftText || '').trim();
        if (!text) return;
        setIsSavingEntry(true);
        try {
            if (editingEntryId) {
                if (!onEditWorkPerformedEntry) return;
                await onEditWorkPerformedEntry(job.id, editingEntryId, text);
                setEditingEntryId(null);
            } else {
                if (!onAddWorkPerformedEntry) return;
                // wpauthor-5d3f8a26 — a promoted entry records WHICH note it came
                // from, and is authored by the PROMOTER. Promoting exists so the
                // reviewer can reword the text before the customer reads it, so
                // attributing it to the note's writer would put their name on
                // sentences they may not have written. The note keeps its own
                // author; sourceNoteId links the two. Only a single-note promote
                // can claim provenance — if several notes were promoted into one
                // entry there is no one source, so none is recorded.
                const sourceNoteId = pendingPromotions.length === 1 ? pendingPromotions[0] : null;
                await onAddWorkPerformedEntry(job.id, text, { sourceNoteId });
            }
            // Clear dirty only on success, so a failed save still warns on close.
            draftDirtyRef.current = false;
            setDraftText('');
            // wppromote-6a3f1e94 — the marker lands only NOW, and only because the
            // entry actually committed. promotionsSurvived is retired here: an
            // entry either got written or it did not, so the old "did the text
            // survive a reword?" heuristic has nothing left to guess at. Still
            // after the fact and best-effort (markNotesPromoted swallows its own
            // errors) — the text is already stored, so a failed tag must never
            // look like a failed save. Closing without saving leaves
            // pendingPromotions to die with the component, which is how
            // "promote, then discard" marks nothing.
            if (pendingPromotions.length > 0) {
                await markNotesPromoted({
                    orgId,
                    noteIds: pendingPromotions,
                    currentUserProfile,
                    targetType: 'job',
                });
                setPendingPromotions([]);
            }
        } catch (err) {
            // wpauthor-5d3f8a26 — SEAM E. The write is a callable now, so a refusal
            // arrives as a thrown error rather than a rules denial swallowed by the
            // SDK. It MUST be surfaced: the draft is still on screen, the dirty flag
            // is still set, and a save that silently did nothing would look exactly
            // like a save that worked. The server's own message is shown verbatim —
            // "this entry was superseded by a change made elsewhere" is precisely
            // what the person needs to read.
            window.alert(err?.message || 'That change could not be saved. Try again.');
        } finally {
            setIsSavingEntry(false);
        }
    };

    const handleRemoveEntryClick = async (entry) => {
        if (!onRemoveWorkPerformedEntry) return;
        // Says what removal actually does, because it is not a delete: the text
        // leaves the customer's invoice and stays on the internal record.
        if (!window.confirm(
            'Remove this from Work Performed?\n\n'
            + 'It comes off the customer\'s invoice straight away, but stays on this job '
            + 'internally — greyed out, showing who wrote it and who removed it.'
        )) return;
        // wpauthor-5d3f8a26 — same reasoning as the save handler: a refused or
        // failed removal must say so, not leave the entry sitting there looking
        // like the click missed.
        try {
            await onRemoveWorkPerformedEntry(job.id, entry.id);
        } catch (err) {
            window.alert(err?.message || 'That entry could not be removed. Try again.');
        }
    };

    // chklstweb-3a7f1c58 — the one completion door for this pop-up. A refusal (the
    // checklist gate, or anything else the rules or server say no to) is SHOWN and the
    // pop-up stays open; it used to close as if the job had completed. onCompleteJob
    // returns false when the person backs out (multi-crew confirm) — stay open then too.
    const runCompletion = async () => {
        setIsCompleting(true);
        try {
            const result = await onCompleteJob(job.id);
            if (result === false) return;
            setShowCompleteConfirm(false);
            setGateDialogOpen(false);
            onClose();
        } catch (err) {
            const msg = await explainChecklistRefusal({ orgId, jobId: job.id, error: err });
            window.alert(msg || err?.message || 'That job could not be completed. Try again.');
        } finally {
            setIsCompleting(false);
        }
    };

    // Owner/Admin/Manager "Complete anyway…": record the override, then complete.
    // A refused override throws to the dialog, which shows the server's sentence.
    const handleOverrideAndComplete = async (reason) => {
        await overrideChecklistGate({ jobId: job.id, reason });
        await runCompletion();
    };

    const handleFinishChecklist = (checklistId) => {
        setGateDialogOpen(false);
        setShowCompleteConfirm(false);
        setChecklistFocus({ checklistId, nonce: Date.now() });
    };

    const handleCompleteConfirmed = async () => {
        // jobtidy-4d8b1f63 — completing tears the modal down, so an unsaved draft
        // would be lost here too.
        if (!confirmDiscardWorkPerformed()) return;
        // chklstweb-3a7f1c58 — ask the checklist gate first, with one fresh read. If the
        // read itself fails, carry on: firestore.rules still refuses a blocked completion.
        if (isChecklistJob) {
            let gate = 'clear';
            try { gate = await readChecklistGate(orgId, job.id); } catch (_) { /* rules still guard */ }
            if (gate === 'blocked') { setGateDialogOpen(true); return; }
        }
        // jobtidy-4d8b1f63 — this used to nag on `job.techNotes` alone. Nothing
        // writes techNotes from web any more, so that test would fire on nearly
        // every completion and become noise. It now asks only when the job carries
        // NO customer-facing record and NO notes of any kind: empty Work Performed
        // (saved or drafted) and an empty Notes & Media list. Legacy techNotes
        // still count, so jobs that predate this keep their old answer.
        // wpauthor-5d3f8a26 — an ACTIVE entry, or the composer's unsaved draft, or
        // the saved mirror (which covers a legacy job whose entries have not been
        // migrated yet). Removed entries deliberately do not count: their text is
        // off the invoice, so the job carries no customer-facing record.
        const hasWorkPerformed = workPerformedEntries.some(isActiveEntry)
            || (draftText || '').trim().length > 0
            || (job.workPerformed || '').trim().length > 0;
        // legnotes9-6b4e8d12 — SEAM 9. The `|| (job.techNotes?.length || 0) > 0`
        // disjunct is gone; the array no longer exists, so it was always false.
        const hasAnyNotes = (jobNotes?.length || 0) > 0;
        if (!hasWorkPerformed && !hasAnyNotes) {
            if (!window.confirm('No notes have been added to this job. Complete anyway?')) return;
        }
        await runCompletion();
    };

    const handleSaveItem = async () => {
        if (!newItem.name.trim() || !onAddTechLineItem) return;
        setIsSavingItem(true);
        await onAddTechLineItem(job.id, newItem);
        setNewItem({ name: '', quantity: 1, price: '' });
        setShowAddItem(false);
        setIsSavingItem(false);
    };

    // --- Render helpers ---

    const renderJobDetails = () => {
        const items = job.lineItems || [];
        const description = job.description;
        if (items.length === 0 && !description) return null;
        return (
            <div className="rounded-lg border border-slate-100 overflow-hidden">
                <div className="bg-slate-50 px-3 py-1.5 border-b border-slate-100">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Scope of Work</p>
                </div>
                <div className="p-3 space-y-2">
                    {items.map((item, i) => (
                        <div key={i} className="flex justify-between items-start">
                            <div className="flex-1 min-w-0">
                                <span className={`block text-sm break-words ${LINE_NAME}`}>{item.name}</span>
                                {item.description && <span className={LINE_DESC}><LineDescription text={item.description} /></span>}
                                {item.addedByTech && (
                                    <span className="inline-block text-[10px] font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded mt-1">
                                        Added on-site by {item.addedByName || 'tech'}
                                    </span>
                                )}
                            </div>
                            <span className="text-xs text-slate-400 ml-3 flex-shrink-0">×{item.quantity}</span>
                        </div>
                    ))}
                    {description && (
                        <p className="text-sm text-slate-500 border-t border-slate-100 pt-2 mt-2 whitespace-pre-line">{description}</p>
                    )}
                </div>
            </div>
        );
    };

    // legnotes9-6b4e8d12 — SEAM 9. renderTechNotes() is GONE, along with its call
    // site below. It rendered a read-only "Job Notes" card from job.techNotes[]
    // and already returned null on every job: Seam 5 stopped writing the array and
    // Seam 8 moved every element into organizations/{orgId}/notes and deleted it.
    // Those notes are still on screen — in the Notes & Media strip further down,
    // with their original author and date.

    // jobtidy-4d8b1f63 — SEAM 2 reaches the quick view. This is where staff
    // actually work a job on web, and until now the only way to write the one
    // customer-facing field was to open the full editor. Mirrors the mobile
    // FieldTechJobDetailScreen box and the JobFormModal field: same label, same
    // sub-label, same placeholder, and the same position relative to Notes &
    // Media (legacy read-only notes -> Work Performed -> Notes & Media) so the
    // two web screens read identically.
    //
    // Job events only — an Appointment or a Follow-Up has no work performed.
    //
    // Read-only once the job is Completed or Canceled, which is not a new gate
    // but the rules stated honestly: firestore.rules techJobUpdatable() lets an
    // assigned tech write a job ONLY while it is Pending / Scheduled / In
    // Progress. Office and above can still edit via Edit Full Details, which is
    // exactly the disjunct (isOffice / ownsCustomer) that stays unguarded there.
    // The text still renders when done, so the record never disappears.
    // wpauthor-5d3f8a26 — SEAM B. The single Work Performed box is now a list of
    // AUTHORED entries. Work Performed used to be one textarea anyone who could
    // edit the job typed into, with nothing recording who wrote what — so when
    // two people worked a job and one wrote a diagnosis that turned out to be
    // wrong, there was no record of who wrote it.
    //
    // THE CUSTOMER STILL READS ONE BLOCK OF TEXT. Nothing here reaches them: the
    // invoice, PDF, printed page and portal all read the flattened mirror, which
    // renders under the label 'Work performed' rather than a person's name
    // (s1-wpfirst-4e9c1b73). Attribution is internal, and the entries are not
    // readable by a portal user at all (rules test WPA10).
    //
    // WHO CAN EDIT WHAT is enforced by firestore.rules, not here — this only
    // decides what to show. `canEditEntry` mirrors the rule: your own entry, or
    // anyone's if you are Office Staff and above. A button this file wrongly
    // showed would simply be refused by the server.
    const renderWorkPerformed = () => {
        if (job.eventType !== 'Job') return null;

        const canWrite = !isJobDone && canEdit && !!onAddWorkPerformedEntry;
        // The exact client mirror of isOffice() in the rules — Office Staff plus
        // Admin/Manager/Owner/System Admin. A mismatch here costs a refused
        // write, never a wrong save: the rules are the real gate.
        const isOfficeOrAbove = canEditAnyWorkPerformedEntry(currentUserProfile);
        const canEditEntry = (entry) =>
            canWrite && !entry.removed && (isOfficeOrAbove || entry.authorId === currentUserId);

        // A legacy job whose entries have not been migrated: text on the field,
        // nothing in the subcollection. Show the text rather than an empty list,
        // and say plainly that nobody knows who wrote it — never attribute it.
        const legacyMirrorOnly =
            workPerformedEntries.length === 0 && (job.workPerformed || '').trim().length > 0;

        const renderEntry = (entry) => {
            const removed = entry.removed === true;
            const byline = describeWorkPerformedAuthor(entry);
            const created = entry.createdAt?.toDate ? entry.createdAt.toDate() : null;
            return (
                <div
                    key={entry.id}
                    className={`rounded-md p-2.5 text-sm border ${
                        removed
                            ? 'bg-slate-50 border-slate-100 opacity-60'
                            : 'bg-white border-slate-200'
                    }`}
                >
                    <p className={`whitespace-pre-line ${removed ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                        {entry.text}
                    </p>
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        <span className={`text-[10px] ${entry.legacy || !entry.authorId ? 'text-slate-400 italic' : 'text-slate-500 font-medium'}`}>
                            {byline}{created ? ` · ${format(created, 'MMM d')}` : ''}
                        </span>
                        {entry.sourceNoteId && !removed && (
                            <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full">
                                From a note
                            </span>
                        )}
                        {removed && (
                            <span className="text-[10px] font-semibold text-slate-500 bg-slate-200 px-1.5 py-0.5 rounded-full">
                                Removed{entry.removedByName ? ` by ${entry.removedByName}` : ''} — not on the invoice
                            </span>
                        )}
                        {canEditEntry(entry) && editingEntryId !== entry.id && (
                            <span className="ml-auto flex items-center gap-2">
                                <button
                                    onClick={() => startEditEntry(entry)}
                                    className="text-[10px] font-semibold text-indigo-600 hover:text-indigo-800"
                                >
                                    Edit
                                </button>
                                <button
                                    onClick={() => handleRemoveEntryClick(entry)}
                                    className="text-[10px] font-semibold text-slate-500 hover:text-rose-600"
                                >
                                    Remove
                                </button>
                            </span>
                        )}
                    </div>
                </div>
            );
        };

        return (
            <div className="rounded-lg border border-slate-100 overflow-hidden">
                <div className="bg-slate-50 px-3 py-1.5 border-b border-slate-100">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                        <CheckCircle size={11} /> Work Performed
                        <span className="normal-case font-normal text-slate-400">— the customer reads this</span>
                    </p>
                </div>
                <div className="p-3 space-y-2">
                    {legacyMirrorOnly && (
                        <div className="rounded-md p-2.5 text-sm border bg-white border-slate-200">
                            <p className="text-slate-700 whitespace-pre-line">{job.workPerformed}</p>
                            <span className="text-[10px] text-slate-400 italic">{WP_NO_AUTHOR_LABEL}</span>
                        </div>
                    )}

                    {workPerformedEntries.map(renderEntry)}

                    {!legacyMirrorOnly && workPerformedEntries.length === 0 && !canWrite && (
                        <p className="text-xs text-slate-400 italic">Nothing recorded yet.</p>
                    )}

                    {canWrite && (
                        <div className="pt-1 space-y-2">
                            {editingEntryId && (
                                <p className="text-[10px] font-semibold text-indigo-600">
                                    Editing an entry — its author does not change.
                                </p>
                            )}
                            <textarea
                                value={draftText}
                                onChange={e => { draftDirtyRef.current = true; setDraftText(e.target.value); }}
                                placeholder={editingEntryId
                                    ? 'Reword this entry.'
                                    : 'What you did on site. This goes on the invoice.'}
                                rows={3}
                                className="w-full text-sm border border-slate-200 rounded-lg p-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-300"
                            />
                            <div className="flex items-center justify-end gap-2">
                                {draftDirtyRef.current && !isSavingEntry && (
                                    <span className="text-[10px] text-amber-600 font-semibold">Unsaved changes</span>
                                )}
                                {editingEntryId && (
                                    <button
                                        onClick={cancelEditEntry}
                                        disabled={isSavingEntry}
                                        className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-800 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                )}
                                <button
                                    onClick={handleSaveEntryClick}
                                    disabled={isSavingEntry || !(draftText || '').trim()}
                                    className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50"
                                >
                                    {isSavingEntry ? 'Saving…' : (editingEntryId ? 'Save changes' : 'Add entry')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderResources = () => {
        const availableResources = (resources || []).filter(r => r.status === 'Available');

        const handleToggleResource = async (resourceId) => {
            if (!onUpdateResources) return;

            // If adding (not removing), check for conflicts first
            const isRemoving = assignedResources.includes(resourceId);
            if (!isRemoving && jobs && job.startAt && job.endAt) {
                const jobStart = job.startAt?.toDate ? job.startAt.toDate() : new Date(job.startAt);
                const jobEnd = job.endAt?.toDate ? job.endAt.toDate() : new Date(job.endAt);
                const jobInterval = { start: jobStart, end: jobEnd };

                const conflictingJob = jobs.find(j => {
                    if (j.id === job.id) return false;
                    if (!(j.assignedResources || []).includes(resourceId)) return false;
                    const otherStart = j.startAt?.toDate ? j.startAt.toDate() : null;
                    const otherEnd = j.endAt?.toDate ? j.endAt.toDate() : null;
                    if (!otherStart || !otherEnd) return false;
                    return (
                        isWithinInterval(otherStart, jobInterval) ||
                        isWithinInterval(otherEnd, jobInterval) ||
                        isWithinInterval(jobStart, { start: otherStart, end: otherEnd }) ||
                        isWithinInterval(jobEnd, { start: otherStart, end: otherEnd })
                    );
                });

                if (conflictingJob) {
                    const r = resources.find(res => res.id === resourceId);
                    const otherStart = conflictingJob.startAt?.toDate ? conflictingJob.startAt.toDate() : new Date(conflictingJob.startAt);
                    const timeStr = otherStart.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
                    alert(`"${r?.name || 'This resource'}" is already assigned to "${conflictingJob.title}" at ${timeStr}. Please choose a different resource.`);
                    return;
                }
            }

            const updated = isRemoving
                ? assignedResources.filter(id => id !== resourceId)
                : [...assignedResources, resourceId];
            setIsSavingResources(true);
            await onUpdateResources(job.id, updated);
            setIsSavingResources(false);
        };

        return (
            <div className="rounded-lg border border-slate-100 overflow-hidden">
                <div className="bg-slate-50 px-3 py-1.5 border-b border-slate-100 flex items-center justify-between">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Assigned Resources</p>
                    {!isJobDone && onUpdateResources && (
                        <button
                            onClick={() => setShowEditResources(p => !p)}
                            className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
                        >
                            {showEditResources ? 'Done' : 'Edit'}
                        </button>
                    )}
                </div>
                <div className="p-3">
                    {!showEditResources ? (
                        // Read-only view — chips
                        assignedResources.length === 0 ? (
                            <p className="text-xs text-slate-400 italic">No resources assigned.</p>
                        ) : (
                            <div className="flex flex-wrap gap-1.5">
                                {assignedResources.map(id => {
                                    const r = resources.find(res => res.id === id);
                                    if (!r) return null;
                                    return (
                                        <span key={id} className="flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full border border-slate-200">
                                            {r.type === 'Vehicle' ? <Truck size={10}/> : <Wrench size={10}/>}
                                            {r.name}
                                        </span>
                                    );
                                })}
                            </div>
                        )
                    ) : (
                        // Edit view — checkboxes
                        <div className="space-y-1.5">
                            {availableResources.map(r => {
                                const isChecked = assignedResources.includes(r.id);
                                return (
                                    <label key={r.id} className="flex items-center gap-2.5 cursor-pointer group">
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => handleToggleResource(r.id)}
                                            disabled={isSavingResources}
                                            className="h-4 w-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-400 disabled:opacity-50"
                                        />
                                        <span className="flex items-center gap-1.5 text-sm text-slate-700 group-hover:text-slate-900">
                                            {r.type === 'Vehicle' ? <Truck size={12} className="text-slate-400"/> : <Wrench size={12} className="text-slate-400"/>}
                                            {r.name}
                                        </span>
                                        <span className="text-xs text-slate-400">{r.type}</span>
                                    </label>
                                );
                            })}
                            {availableResources.length === 0 && (
                                <p className="text-xs text-slate-400 italic">No available resources.</p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderAddItem = () => {
        if (isJobDone || !onAddTechLineItem) return null;
        return (
            <div className="rounded-lg border border-slate-100 overflow-hidden">
                <button
                    onClick={() => setShowAddItem(p => !p)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                        <Plus size={11}/> Add Item On-Site
                    </span>
                    {showAddItem ? <ChevronUp size={13} className="text-slate-400"/> : <ChevronDown size={13} className="text-slate-400"/>}
                </button>
                {showAddItem && (
                    <div className="p-3 space-y-2 border-t border-slate-100">
                        <p className="text-xs text-slate-400">Flagged for office review before invoicing.</p>
                        <input
                            type="text"
                            placeholder="Item name (e.g. Extra valve)"
                            value={newItem.name}
                            onChange={e => setNewItem(p => ({ ...p, name: e.target.value }))}
                            className="w-full text-sm border border-slate-200 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                        />
                        <div className="flex gap-2">
                            <div className="flex-1">
                                <label className="text-xs text-slate-400 mb-1 block">Qty</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={newItem.quantity}
                                    onChange={e => setNewItem(p => ({ ...p, quantity: e.target.value }))}
                                    className="w-full text-sm border border-slate-200 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                />
                            </div>
                            <div className="flex-1">
                                <label className="text-xs text-slate-400 mb-1 block">Price ($)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="0.00"
                                    value={newItem.price}
                                    onChange={e => setNewItem(p => ({ ...p, price: e.target.value }))}
                                    className="w-full text-sm border border-slate-200 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                />
                            </div>
                        </div>
                        <button
                            onClick={handleSaveItem}
                            disabled={isSavingItem || !newItem.name.trim()}
                            className="w-full py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50"
                        >
                            {isSavingItem ? 'Adding…' : 'Add to Job'}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const renderSessionHistory = () => {
        if (closedPunches.length === 0) return null;
        return (
            <div className="mt-3 rounded-lg border border-slate-100 overflow-hidden">
                <div className="bg-slate-50 px-3 py-1.5 border-b border-slate-100">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Your Sessions</p>
                </div>
                <div className="divide-y divide-slate-50">
                    {closedPunches.map((punch, i) => {
                        const pIn = safeToDate(punch.punchIn);
                        const pOut = safeToDate(punch.punchOut);
                        return (
                            <div key={i} className="flex items-center justify-between px-3 py-2 bg-white">
                                <div className="flex items-center gap-2">
                                    <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-500 text-xs flex items-center justify-center font-semibold">{i + 1}</span>
                                    <span className="text-sm text-slate-600">
                                        {pIn ? format(pIn, 'h:mm a') : '—'}
                                        <span className="text-slate-400 mx-1">→</span>
                                        {pOut ? format(pOut, 'h:mm a') : '—'}
                                    </span>
                                </div>
                                <span className="text-sm font-semibold text-slate-700">
                                    {formatSessionDuration(punch.hours)}
                                </span>
                            </div>
                        );
                    })}
                    <div className="flex items-center justify-between px-3 py-2 bg-slate-50">
                        <span className="text-xs font-semibold text-slate-500">Total logged</span>
                        <span className="text-sm font-bold text-slate-800">
                            {formatSessionDuration(totalClockedHours)}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    const renderControls = () => {
        const status = job.status;

        if (status === 'Completed' || status === 'Canceled') {
            return (
                <div className="space-y-3 w-full">
                    {renderSessionHistory()}
                    <p className="text-sm font-semibold text-slate-500 flex items-center gap-2">
                        <CheckCircle size={15} className="text-emerald-500" />
                        This job is {status}.
                    </p>
                </div>
            );
        }

        if (status === 'Pending') {
            return (
                <p className="text-sm font-semibold text-amber-600 flex items-center gap-2">
                    <Clock size={15} />
                    Awaiting customer confirmation — job cannot be started until Scheduled.
                </p>
            );
        }

        if (clockRunning) {
            return (
                <div className="space-y-3 w-full">
                    {renderSessionHistory()}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-lg">
                            <Clock size={16} className="text-slate-500" />
                            <span className="font-mono text-base font-semibold text-slate-700">{elapsedTime}</span>
                        </div>
                        <button
                            onClick={handlePunchOut}
                            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white rounded-lg font-bold transition-colors"
                        >
                            <Square size={14} /> Punch Out
                        </button>
                    </div>
                </div>
            );
        }

        if (!hasAnyPunches) {
            return (
                <button
                    onClick={handlePunchIn}
                    className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white rounded-lg font-bold transition-colors shadow-sm shadow-emerald-200"
                >
                    <Play size={16} /> Start Job
                </button>
            );
        }

        if (!showCompleteConfirm) {
            return (
                <div className="space-y-3 w-full">
                    {renderSessionHistory()}
                    <div className="flex items-center justify-between gap-3 pt-1">
                        <button
                            onClick={handlePunchIn}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold text-sm transition-colors"
                        >
                            <LogIn size={14} /> Punch In Again
                        </button>
                        <button
                            onClick={() => setShowCompleteConfirm(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold text-sm transition-colors"
                        >
                            <CheckCircle size={14} /> Complete Job
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div className="w-full rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
                <div className="flex items-start gap-2">
                    <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-semibold text-amber-900">Mark this job as complete?</p>
                        <p className="text-xs text-amber-700 mt-0.5">
                            This will finalise all time entries. You won't be able to punch in again once completed.
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 justify-end">
                    <button
                        onClick={() => setShowCompleteConfirm(false)}
                        className="px-3 py-1.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleCompleteConfirmed}
                        disabled={isCompleting}
                        className="px-4 py-1.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-60"
                    >
                        {isCompleting ? 'Completing…' : 'Yes, Complete Job'}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="flex justify-between items-start p-5 border-b border-slate-100 flex-shrink-0">
                    <div>
                        {/* jobnumdisp-6a1e9c34 — number ahead of the title, muted, matching
                            the TimesheetsView.js:1012 treatment. Renders nothing at all when
                            the job has no number yet (fresh save, or a recurring projection),
                            so the header falls back to exactly the old title-only line. */}
                        <h2 className="text-lg font-bold text-slate-800">
                            {formatJobNumber(job.jobNumber) && (
                                <span className="text-slate-400 mr-1.5">{formatJobNumber(job.jobNumber)}</span>
                            )}
                            {job.title}
                        </h2>
                        {job.status && (
                            <span className={`inline-block mt-1 text-xs font-semibold px-2 py-0.5 rounded-full
                                ${job.status === 'Completed' ? 'bg-emerald-100 text-emerald-700' :
                                  job.status === 'In Progress' ? 'bg-indigo-100 text-indigo-700' :
                                  job.status === 'Canceled' ? 'bg-red-100 text-red-700' :
                                  'bg-slate-100 text-slate-600'}
                            `}>
                                {job.status}
                            </span>
                        )}
                    </div>
                    {/* jobtidy-4d8b1f63 — guarded: warns before discarding an unsaved
                        Work Performed draft. */}
                    <button onClick={handleAttemptClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
                        <X size={18} />
                    </button>
                </div>

                {/* Scrollable Body */}
                <div className="overflow-y-auto flex-1 p-5 space-y-4">
                    {/* taxexempt3-8c4f2a1d — the customer's tax exemption for this job (staff only) */}
                    <TaxExemptionBanner kind="job" document={job} orgId={orgId || currentUserProfile?.organizationId} currentUserProfile={currentUserProfile} />
                    {/* Customer / Address */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Customer</p>
                            <p className="text-sm font-medium text-slate-700">{job.clientName || '—'}</p>
                        </div>
                        <div>
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Address</p>
                            <p className="text-sm font-medium text-slate-700">{job.address || '—'}</p>
                        </div>
                        {/* calclar-5e2a8d14 — who is on this job, by name (the calendar only shows a
                            person's column when the Team filter ticks them). */}
                        <div className="col-span-2">
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Assigned to</p>
                            <ul className="text-sm font-medium text-slate-700 space-y-0.5">
                                {assignedToList(job).map(n => <li key={n}>{n}</li>)}
                            </ul>
                        </div>
                    </div>

                    {/* Scope of work */}
                    {renderJobDetails()}

                    {/* Resources */}
                    {renderResources()}

                    {/* jobtidy-4d8b1f63 — Work Performed sits above Notes & Media, the
                        same relative position it holds in JobFormModal. legnotes9-6b4e8d12
                        removed the legacy read-only notes card that used to precede it. */}
                    {renderWorkPerformed()}

                    {/* chklstweb-3a7f1c58 — Checklists, between Work Performed and Notes & Media. Jobs only. */}
                    {isChecklistJob && job?.id && orgId && (
                        <JobChecklistsPanel
                            job={job}
                            orgId={orgId}
                            currentUserProfile={currentUserProfile}
                            currentUserId={currentUserId}
                            checklists={jobChecklists}
                            state={checklistState}
                            focusRequest={checklistFocus}
                        />
                    )}

                    {/* r1-3i-jqvm-strip-8d3c5a92 — Notes & Media, mirror of QuoteDetailsView
                        r1-1a strip. Flagging ON so orgUsers populates the Notify picker
                        (writes FO-4 flagReviewerIds). Attached to job:<id>.
                        jobtidy-4d8b1f63 — this is now the ONLY note writer on this screen;
                        the legacy composer that used to sit beside it is gone. */}
                    {job?.id && orgId && (
                        <div>
                            <button
                                type="button"
                                onClick={() => setJobNotesExpanded(v => !v)}
                                className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-white rounded-lg shadow-sm border border-slate-100 hover:bg-slate-50"
                            >
                                <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                                    <MessageSquare size={16} /> Notes &amp; Media
                                    <span className="text-xs font-normal text-slate-400">({jobNotes.length})</span>
                                </span>
                                {jobNotesExpanded ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
                            </button>
                            {jobNotesExpanded && (
                                <div className="mt-2 bg-white rounded-lg shadow-sm border border-slate-100 p-4 space-y-3">
                                    <NoteComposer
                                        attachedTo={[{ type: 'job', id: job.id }]}
                                        orgId={orgId}
                                        currentUserProfile={currentUserProfile}
                                        orgUsers={orgUsers}
                                        placeholder="Add a note or photo about this job..."
                                    />
                                    <div className="space-y-2 pt-1">
                                        {jobNotes.length === 0 ? (
                                            <p className="text-sm text-slate-500 text-center py-4">No notes yet — add the first one above.</p>
                                        ) : (
                                            /* wppromote-6a3f1e94 — onPromote ONLY when the Work Performed box
                                               is actually editable on this screen: Job events only (render-
                                               WorkPerformed returns null otherwise) and not Completed/Canceled.
                                               That is the same `canWrite` test the box itself uses, so the
                                               button can never appear with nothing to fill. */
                                            jobNotes.map(note => (
                                                <NoteCard
                                                    key={note.id}
                                                    note={note}
                                                    currentUserProfile={currentUserProfile}
                                                    orgUsers={orgUsers}
                                                    orgId={orgId}
                                                    onPromote={(job.eventType === 'Job' && !isJobDone && canEdit && !!onAddWorkPerformedEntry) ? handlePromoteNote : undefined}
                                                    promotePending={pendingPromotions.includes(note.id)}
                                                />
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Add Item */}
                    {renderAddItem()}

                    {/* Punch controls */}
                    <div className="border-t border-slate-100 pt-4">
                        {renderControls()}
                    </div>
                </div>

                {/* Footer */}
                {canEdit && (
                    <div className="flex justify-end px-5 py-3 border-t border-slate-100 flex-shrink-0">
                        {/* jobtidy-4d8b1f63 — guarded: navigating to the full editor tears
                            this modal down, so an unsaved draft would be lost. */}
                        <button
                            onClick={handleEditFullDetails}
                            className="flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
                        >
                            <Edit size={13} /> Edit Full Details
                        </button>
                    </div>
                )}
            </div>
            {/* chklstweb-3a7f1c58 — "This job can't be completed yet" */}
            <ChecklistGateDialog
                isOpen={gateDialogOpen}
                onClose={() => setGateDialogOpen(false)}
                checklists={jobChecklists}
                state={checklistState}
                canOverride={canOverrideChecklist(currentUserProfile)}
                isCompleting={isCompleting}
                onFinishIt={handleFinishChecklist}
                onOverrideAndComplete={handleOverrideAndComplete}
                onCompleteNow={runCompletion}
            />
        </div>
    );
};

// taxexempt3-8c4f2a1d — after "Charge tax on this document" / "Apply exemption again" the page shows the
// document as the server now has it (TaxExemptionBanner re-reads it), without closing the page.
export default withExemptionRefresh(JobQuickViewModal, 'job');